import { createDoseProp } from '../utils/dose-prop'
import { isString } from '../utils/is'
import {
  removeKeysWithNullishValues,
  replaceObjectRawKeys,
} from '../utils/object'
import { createSelector } from '../utils/selector-utils'
import {
  actionAddOralSedativeDose,
  selectOralSedativeDosesAdministeredByDrugId,
} from './drugs-administered'

const prop = createDoseProp({
  route: 'oral',
  drugInfoProp: 'oralSedatives',
  mapFn: replaceObjectRawKeys,
})

/**
 * @param {OralSedative} oralSedative The drug we're selecting
 * @returns {any} The changes to apply to the case.
 */
export const actionSelectOralSedative = ({ id, name, unit }) =>
  prop.actionSelect(id, { name, unit })

/**
 * @param {string} id The rxcui ID of the drug we're unselecting
 * @returns {any} The changes to apply to the case.
 */
export const actionUnselectOralSedative = prop.actionUnselect

/**
 * @param {string} id The rxcui ID of the drug we're unselecting
 * @param {{
 *   amount_raw?: string
 *   route?: OralSedativeRoute
 * }} updates
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateOralSedativeDosage = (id, updates) =>
  prop.actionUpdateDosage(id, updates)

/**
 * @param {string} id
 * @param {OralSedativeRoute} route
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateOralSedativeRoute = (id, route) =>
  prop.actionUpdateDosage(id, { route })

/**
 * @param {string} id
 * @param {string} amount_raw
 * @returns {any} The changes to apply to the case.
 */
export const actionUpdateOralSedativeAmount = (id, amount_raw) =>
  prop.actionUpdateDosage(id, { amount_raw })

/**
 * @param {MacroOralSedative} macroOralSedative
 * @returns {any} The changes to apply to the case.
 */
export const actionPresetOralSedative = ({
  id,
  name,
  unit,
  route,
  dose,
  preProcedure,
}) => {
  let changes = actionSelectOralSedative({ id, name, unit })

  /**
   * Pre-procedure sedatives work differently in that they actually create a new
   * dose in editing mode. Normal mid-case sedatives simply select the drug and
   * update its dosage without recording a dose.
   */
  if (preProcedure) {
    changes = {
      ...changes,
      ...actionAddOralSedativeDose({
        drugId: id,
        route: route || 'PO',
        amount_raw: dose || null,
        time: null,
        unit: unit || 'mg',
        editing: true,
        shouldStartCase: false,
      }),
    }
  } else {
    changes = {
      ...changes,
      ...actionUpdateOralSedativeAmount(id, dose),
      ...actionUpdateOralSedativeRoute(id, route),
    }
  }

  return changes
}

/** @type {CaseSelector<CaseData['oralSedatives']>} */
export const selectCaseOralSedatives = prop.selectDrugInfoRaw

/** @type {CaseSelector<{ [drugId: string]: OralDrugDosageBase }>} */
export const selectOralSedativeDosage = prop.selectDosage

/** @type {CaseSelector<{ [drugId: string]: OralDrugDosage[] }>} */
// @ts-ignore
export const selectSelectedOralSedatives = prop.selectSelectedDrugs

/** @type {CaseSelector<{ [drugId: string]: OralDrugDosage }>} */
export const selectSelectedOralSedativesById = prop.selectSelectedDrugsById

/** @type {CaseSelector<MacroOralSedative[]>} */
export const selectOralSedativesForMacro = createSelector(
  selectSelectedOralSedatives,
  selectOralSedativeDosage,
  selectOralSedativeDosesAdministeredByDrugId,
  (drugs, dosage, administeredByDrugId) =>
    drugs.map(({ id, name, unit }) => {
      const { amount_raw, route } = dosage[id] || {}
      const administered = administeredByDrugId[id] || []
      /**
       * We use this as the indicator for whether the drug was given before
       * procedure started or not. Any oral sedative given during the case will
       * have shouldStartCase equal to true. We don't use the isPreProcedure
       * flag because that is only determined once we have a case start time. If
       * they are building a preset in the preset mode (or tour mode) it would
       * be weird if they had to start a case in order to save a pre-procedure
       * sedative preset.
       */
      const shouldStart = administered.some(
        dose => dose.shouldStartCase === false
      )
      const firstDose = administered[0]
      let dose = (firstDose && firstDose.amount_raw) || amount_raw
      if (isString(dose) && dose.trim() === '0') {
        dose = null
      }
      return removeKeysWithNullishValues({
        id,
        name,
        unit,
        preProcedure: shouldStart || null,
        dose,
        route: (firstDose && firstDose.route) || route,
      })
    })
)
